// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { sum } from 'lodash-es';
import { DEFAULT_TRAJECTORY_FUNCTION_TYPE } from '../../../../../app.constants';
import { setUnsetParameters } from '../../../../../functions/board-parameters/set-unset-parameters';
import { holdsActiveCustomModel } from '../../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsActiveTransitionTargetData } from '../../../../../functions/client-model/client-node/holds-active-transition-target-data';
import { holdsCustomModel } from '../../../../../functions/client-model/client-node/holds-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { getTotalEmissionsFromInventoryTree } from '../../../../../functions/emission-inventory/emission-inventory-helpers';
import { getCalculatedEmissionFactors } from '../../../../../functions/factors/factor-helpers';
import { getBoardYearsFromTimeline } from '../../../../../functions/timeline/get-board-years-from-timeline';
import { getLengthOfTimeline } from '../../../../../functions/timeline/get-length-of-timeline';
import { getTransitionYearsFromTimeline } from '../../../../../functions/timeline/get-transition-years-from-timeline';
import { arrayOfDictsToDictOfArrays } from '../../../../../functions/utilities/array-of-dicts-to-dict-of-arrays';
import { lerp } from '../../../../../functions/utilities/lerp';
import { BoardTimeline } from '../../../../../model/board-timeline';
import { CalculatedValue } from '../../../../../model/calculated-value';
import { ClientNode } from '../../../../../model/client-model/client-node';
import { ClientParameter } from '../../../../../model/client-model/client-parameter';
import { CustomTargetModelData } from '../../../../../model/custom-model/custom-target-model-data';
import { Dict } from '../../../../../model/dict';
import { EmissionFactorCalculationMethod } from '../../../../../model/enums/emission-factor-calculation-method';
import { TransitionElement } from '../../../../../model/enums/transition-element';
import { ClientBoardContainer } from '../../../../../public/dashboard/shared/model/client-board-container';
import { Tree } from '../../../../../services/backend/model/tree';
import { CccAdapterTransitionTargetItem } from '../../../../../services/climate-engine/ccc-adapter/ccc-adapter-transition-target-item';
import { CccAdapterService } from '../../../../../services/climate-engine/ccc-adapter/ccc-adapter.service';
import { KeyMap } from '../../../../../services/climate-engine/ccc-algorithm/key-map';
import { TransitionData } from '../../../../../services/climate-engine/ccc-algorithm/transition-data';
import { CCCModelUtility } from '../../../../../services/climate-engine/ccc-model-utility/ccc-model-utility';
import { CalculationResult } from '../../../../../services/climate-engine/model/calculation-result';
import { TargetedEmissionsService } from '../../../../../services/targeted-emissions/targeted-emissions.service';
import { getLeafNodes } from '../../../functions/get-leaf-nodes';
import { getTransitionTargets } from '../../../functions/get-transition-targets';
import { getYearValuesFromDict } from '../../../functions/get-year-values-from-dict';
import { CustomModelDataService } from '../../custom-model-data/custom-model-data.service';
import { getCustomModelTargetsWithLhsCccs } from './get-custom-model-targets';
import { getFirstLowPointIndex } from './get-first-low-point-index';
import { getOneYearChartData } from './get-one-year-chart-data';
import { YearByYearNodeData } from './year-by-year.node-data';

export interface OneYearCustomTargetModelData extends Omit<CustomTargetModelData, 'chartData'> {
  chartData: {
    carbonAbatement: number;
    emissions: number;
    target: number;
    outcome: number;
  };
}
interface EmissionData {
  targetedEmissions: Dict<number>;
  remainingEmissions: Dict<number>;
}

interface TTEmissionData {
  targetedEmissions: number;
  netCarbonAbatement: number;
}
interface DashboardCalculationResult {
  transitions: Dict<TransitionData[]>;
  customModelTransitions: Dict<CustomTargetModelData>;
  TTEmissionData: Dict<TTEmissionData[]>;
  totalStartYearEmissions?: number;
}

@Injectable({
  providedIn: 'root',
})
export class YearByYearService {
  constructor(
    private _cccAdapterService: CccAdapterService,
    private _cccModelUtility: CCCModelUtility,
    private _customModelDataService: CustomModelDataService,
    private _targetedEmissionsService: TargetedEmissionsService
  ) {}

  create(
    board: ClientBoardContainer,
    isPanorama = false,
    tree?: Tree
  ): { yearByYearNodeData: Dict<YearByYearNodeData>; totalStartyearEmissions: number } {
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const rawData = this._calculateData(board, isPanorama, tree);
    const leafNodes = getLeafNodes(board);
    const dict: Dict<YearByYearNodeData> = {};
    leafNodes.forEach((node) => {
      let data: YearByYearNodeData | undefined;
      if (holdsInitialisedUtilisationTE(node)) {
        data = this._createDataForTransitionTarget(node, rawData, timeline);
      } else if (holdsCustomModel(node)) {
        data = this._createDataForCustomModel(node, board, rawData, timeline, isPanorama);
      }
      dict[node.id] = data;
    });
    return { yearByYearNodeData: dict, totalStartyearEmissions: rawData.totalStartYearEmissions };
  }

  private _adjustTargetedEmissions(
    targetedEmissions: Dict<number[]>,
    calculationResults: CalculationResult[]
  ): Dict<number[]> {
    const adjustedTargetedEmissions: Dict<number[]> = {};
    const transitions = arrayOfDictsToDictOfArrays(calculationResults.map((c) => c.transitions));
    Object.entries(targetedEmissions).forEach(([key, values]) => {
      //This is the place to do adjustments if charts looks wrong. Disable the adjustments `initialMultiplier = 1` and ``adjustmentMultiplier = 1`.
      //Also disable the check that ensure that targeted emissions are at least the same as abatement.
      //If it still looks bad, adjustments to the targeted emissions service is needed.

      //This is by no means a good solution but it is here to buy us some time before we have to change how we display emissions in the dashboard.

      //We're adjusting  down to better fit how we want the dashboard to function right now. i.e. having the nodes sum up to a total.
      //Targeted emissions is not meant to work that way, by adjusting down the overdraft that will occur when summing targeted emissions.

      //The most important part here is to lower the emissions for the start year and keep the targeted emissions to keep their rather linear curve.
      //It is important that the adjustments are small and won't cause the targeted emissions to go below the abatement.
      const abatement: { net?: number; gross?: number }[] = transitions[key].map((t) => t.carbonAbatement);
      if (this._cccModelUtility.isTransitionElementShift(key as TransitionElement)) {
        //We do not wan't to use the abatement as a limiting factor since then it is too easy to distort the curve of the targeted emissions.
        //We can however use it as an indication for when it no longer is safe to adjust the values.
        const abatementValues = abatement.map((a) => a.gross ?? a.net ?? 0);
        const remainingEmissions = values.map((v, i) => v - abatementValues[i]);
        let targetIndex = 0;
        for (let i = 0; i < remainingEmissions.length; i++) {
          targetIndex = i;
          if (values[i] <= 0 || remainingEmissions[i] / values[i] < 0.1) {
            break;
          }
        }
        const initialMultiplier = 0.9;
        values = values.map((v, i) => {
          const t = targetIndex <= 0 ? 0 : i / targetIndex;
          return v * lerp(initialMultiplier, 1, t);
        });
      } else {
        //Here we have to look at the low point, since most of the bau for a non-shift will go down, since shifts will remove more than bau counts up.
        //This won't cover all scenarios, but will handle plans that are more reasonable.
        //We'll do a simple adjust looks on the first low point in the values,
        //this is because manual curve adjustments are a bit imprecise and even this can cause the low point of the emissions to be in the middle.
        const adjustmentMultiplier = 0.45;
        const indexForLow = getFirstLowPointIndex(values);
        const low = values[indexForLow];
        const high = low + (values[0] - low) * adjustmentMultiplier;
        values = values.map((v, i) => {
          const t = indexForLow === 0 ? 0 : i / indexForLow;
          return lerp(high, low, t);
        });
      }

      values = values.map((v, i) => {
        const a = abatement[i];
        const netAbatement = a.net ?? a.gross ?? 0;
        return Math.max(v, netAbatement);
      });
      adjustedTargetedEmissions[key] = values;
    });
    return adjustedTargetedEmissions;
  }

  private _calculateData(board: ClientBoardContainer, isPanorama: boolean, tree: Tree): DashboardCalculationResult {
    const distributeUntargetedEmissions = !board.boardSettings.doNotDistributeUntargeted;
    const timeline: BoardTimeline = board.boardSettings.timeline;
    const targets = this._getTargets(board);
    const ttKeys = targets.map((t) => t.transitionElement);
    let targetedEmissions: Dict<number[]> = {};
    const useNew = board.boardSettings.useNewTargetedEmissions;
    if (useNew) {
      //This will run climate cortex to calculate targeted emissions, initCccAdapterService must be run after this.
      targetedEmissions = this._targetedEmissionsService.calculateSimple(board);
    }

    this._initCccAdapterService(targets, timeline, board.boardSettings.electricityEmissionFactorType, board.parameters);
    const transitions: Dict<TransitionData[]> = {};
    const TTEmissionData: Dict<TTEmissionData[]> = {};
    const years = getTransitionYearsFromTimeline(timeline);
    const filledCustomModelTargets = !isPanorama
      ? getCustomModelTargetsWithLhsCccs(board).reduce<{
          [nodeId: string]: CustomTargetModelData;
        }>((obj, cm) => {
          obj[cm.id] = this._customModelDataService.createValidChartDataV3(
            cm.nodeProperties.customTargetModelData,
            board,
            timeline,
            isPanorama
          );
          return obj;
        }, {})
      : {};

    const activeTTsCccs: Dict<string[]> = {};
    const lhsCccsTargetedByTTs: Dict<string[]> = {};
    ttKeys.forEach((ttKey: TransitionElement) => {
      // Getting lhs cccs to be able to see how big part of BAU emissions are coming from each target this is then being used to distribute the remaining emissions
      const lhsCccs = this._cccModelUtility.getTransitionElementsTargetedCccIds(ttKey);
      lhsCccs.forEach((cccId) => {
        const ids = lhsCccsTargetedByTTs[cccId] ?? [];
        lhsCccsTargetedByTTs[cccId] = [...ids, ttKey];
      });
      activeTTsCccs[ttKey] = lhsCccs;
    });
    let emissionDatas: EmissionData[] = [];
    const calculationResults: {
      calculationResult: CalculationResult;
      customModelTargetItems: Dict<OneYearCustomTargetModelData>;
      cccEmissionsBau: Dict<number>;
    }[] = [];
    const endYearWeightsCalculationResult: CalculationResult[] = [];

    let calculatedEmissionFactors: KeyMap<CalculatedValue> = {};

    years.forEach((y, t) => {
      let calculationResult: CalculationResult;
      let endYearResults: CalculationResult;
      let emissionData: EmissionData;
      let cccEmissionsBau: Dict<number>;
      if (useNew) {
        calculationResult = this._cccAdapterService.calculate(t);
        cccEmissionsBau = this._cccAdapterService.getCccEmissionBau(t);
      } else {
        const result = this._cccAdapterService.calculate(t, false, undefined, true);
        calculationResult = result.calculationResult;
        endYearResults = result.endYearWeightsCalculationResult;
        cccEmissionsBau = this._cccAdapterService.getCccEmissionBau(t);
        emissionData = this._getEmissionData(
          activeTTsCccs,
          lhsCccsTargetedByTTs,
          cccEmissionsBau,
          result.calculationResult,
          result.endYearWeightsCalculationResult
        );
      }

      emissionDatas.push(emissionData);
      calculationResults.push({
        calculationResult,
        customModelTargetItems: getOneYearChartData(filledCustomModelTargets, y),
        cccEmissionsBau,
      });
      endYearWeightsCalculationResult.push(endYearResults);

      if (t === 0) {
        calculatedEmissionFactors = getCalculatedEmissionFactors(calculationResult.resources);
      }
    });

    if (useNew) {
      targetedEmissions = this._adjustTargetedEmissions(
        targetedEmissions,
        calculationResults.map((c) => c.calculationResult)
      );
      emissionDatas = calculationResults.map((c, t) => {
        return this._getEmissionDataFromTargetedEmissions(
          activeTTsCccs,
          lhsCccsTargetedByTTs,
          c.cccEmissionsBau,
          targetedEmissions,
          t
        );
      });
    }

    calculationResults.forEach((result, index) => {
      Object.entries(result.calculationResult.transitions).forEach(([id, transition]) => {
        const list = transitions[id] ?? [];
        list.push(transition);
        transitions[id] = list;
      });
      const emissionData = emissionDatas[index];
      const remainingEmissions = { ...result.cccEmissionsBau, ...emissionData.remainingEmissions };
      Object.entries(
        this._getTTEmissionData({
          ttKeys: ttKeys,
          targetedEmissions: emissionData.targetedEmissions,
          remainingEmissions,
          activeTTsCccs,
          lhsCccsTargetedByTTs,
          calculationResult: result.calculationResult,
          endYearWeightsResult: endYearWeightsCalculationResult[index],
          customModelTargetItems: result.customModelTargetItems,
          distributeUntargetedEmissions,
          useNew,
        })
      ).forEach(([id, emissions]) => {
        const list = TTEmissionData[id] ?? [];
        list.push(emissions);
        TTEmissionData[id] = list;
      });
    });

    const totalStartYearEmissions = tree
      ? getTotalEmissionsFromInventoryTree(tree, board.parameters, this._cccModelUtility, calculatedEmissionFactors)
      : 0;

    return {
      transitions,
      TTEmissionData,
      customModelTransitions: filledCustomModelTargets,
      totalStartYearEmissions,
    };
  }

  private _createDataForCustomModel(
    node: ClientNode,
    board: ClientBoardContainer,
    calculations: DashboardCalculationResult,
    timeline: BoardTimeline,
    isPanorama = false
  ): YearByYearNodeData | undefined {
    if (calculations.TTEmissionData[node.id]) {
      const boardYears: number[] = getBoardYearsFromTimeline(timeline);
      const data = calculations.TTEmissionData[node.id];

      const outcomeValues: Dict<number> | undefined = calculations.customModelTransitions[node.id]?.chartData?.outcome;
      const targetValues = getYearValuesFromDict(
        calculations.customModelTransitions[node.id].chartData.target,
        boardYears
      );
      return this._createDataFromCalculationData(outcomeValues, timeline, targetValues, data);
    } else {
      if (!holdsActiveCustomModel(node)) {
        return undefined;
      }
      const boardYears: number[] = getBoardYearsFromTimeline(timeline);
      const customData = this._customModelDataService.createValidChartDataV3(
        node.nodeProperties.customTargetModelData,
        board,
        timeline,
        isPanorama
      );
      const chartData = customData.chartData;
      const emissionValues = getYearValuesFromDict(chartData.emissions, boardYears);
      const abatementValues = getYearValuesFromDict(chartData.carbonAbatement, boardYears);
      const allTargetValues = getYearValuesFromDict(chartData.target, boardYears);
      const outcome = getYearValuesFromDict(chartData.outcome, boardYears, true);

      return {
        emissionBau: emissionValues,
        netCarbonAbatement: abatementValues,
        transition: {
          target: allTargetValues,
          outcome,
        },
      };
    }
  }

  private _createDataForTransitionTarget(
    node: ClientNode,
    calculations: DashboardCalculationResult,
    timeline: BoardTimeline
  ): YearByYearNodeData | undefined {
    if (!holdsActiveTransitionTargetData(node)) {
      return undefined;
    }
    const tt = node.nodeProperties.transitionElement!;
    const data = calculations.TTEmissionData[tt];
    if (!data) {
      return undefined;
    }

    const outcomeValues: Dict<number> | undefined =
      node.nodeProperties.transitionTargetData?.transitionOutcome?.outcome;
    const precedingSeries = new Array(timeline.scenarioStartYear - timeline.boardStartYear).fill(undefined);
    const timelineSeries = calculations.transitions[tt]
      ? calculations.transitions[tt].map((d) => d.activityShift)
      : new Array(getLengthOfTimeline(timeline)).fill(undefined);
    const targetValues = [...precedingSeries, ...timelineSeries];

    return this._createDataFromCalculationData(outcomeValues, timeline, targetValues, data);
  }

  private _createDataFromCalculationData(
    outcomeValues: Dict<number>,
    timeline: BoardTimeline,
    target: number[],
    data: TTEmissionData[]
  ) {
    const boardYears = getBoardYearsFromTimeline(timeline);
    const outcome = outcomeValues ? getYearValuesFromDict(outcomeValues, boardYears, true) : undefined;
    const precedingSeries = new Array(timeline.scenarioStartYear - timeline.boardStartYear).fill(undefined);
    const emissionBau = [...precedingSeries, ...data.map((d) => d.targetedEmissions)];
    const netCarbonAbatement = [...precedingSeries, ...data.map((d) => d.netCarbonAbatement)];
    return {
      emissionBau,
      netCarbonAbatement,
      transition: {
        target,
        outcome,
      },
    };
  }

  private _getEmissionData(
    activeTTsCccs: Dict<string[]>,
    lhsCccsTargetedByTTs: Dict<string[]>,
    cccsEmissionsBau: Dict<number>,
    calculationResult: CalculationResult,
    endYearWeightsCalculationResult: CalculationResult
  ): EmissionData {
    const targetedEmissions: Dict<number> = {};
    const remainingEmissions: Dict<number> = {};

    const addTargetedEmission = (ttId: string, emissions: number) => {
      targetedEmissions[ttId] = (targetedEmissions[ttId] ?? 0) + emissions;
    };

    //Assign Targeted emissions to the tt ids.
    const lhsCccKeys: string[] = Object.keys(lhsCccsTargetedByTTs);
    lhsCccKeys.forEach((cccKey) => {
      const cccEmissionsBau = cccsEmissionsBau[cccKey];
      const ttKeysTargeting: string[] = lhsCccsTargetedByTTs[cccKey] ?? [];

      let claimedEmissions = 0;
      //Assign target emissions for a based on the abatement done.
      //The weight here is always according to the end year otherwise we would only assign the current abatement for a given year.
      ttKeysTargeting.forEach((ttKey) => {
        const lhsCccs = activeTTsCccs[ttKey];
        const totalCccLhsEmissionsBau = sum(lhsCccs.map((cccKey) => cccsEmissionsBau[cccKey] ?? 0));
        //We must check that the abatement for the end year isn't lower than the abatement for the current year.
        //If that is the case we must use the abatement for the current year, otherwise we'll incorrectly end up with negative emissions.
        const currentYearAbatement = calculationResult.transitions[ttKey]?.carbonAbatement.gross ?? 0;
        const endYearWeightAbatement = endYearWeightsCalculationResult.transitions[ttKey]?.carbonAbatement.gross ?? 0;
        const ttAbatement =
          currentYearAbatement > endYearWeightAbatement ? currentYearAbatement : endYearWeightAbatement;
        const part = totalCccLhsEmissionsBau === 0 ? 0 : ttAbatement / totalCccLhsEmissionsBau;
        const targetedEmissions = part * cccEmissionsBau;
        claimedEmissions += targetedEmissions;
        addTargetedEmission(ttKey, targetedEmissions);
      });

      //If the ccs hasn't been targeted fully the remaining emissions is distributed among the tts targeting it.
      //This sets up a map to be able to handle the distribution
      let remaining = cccEmissionsBau - claimedEmissions;
      if (remaining < 0) {
        remaining = 0;
      }
      remainingEmissions[cccKey] = remaining;
    });
    return { targetedEmissions, remainingEmissions };
  }

  private _getEmissionDataFromTargetedEmissions(
    activeTTsCccs: Dict<string[]>,
    lhsCccsTargetedByTTs: Dict<string[]>,
    cccsEmissionsBau: Dict<number>,
    yearlyTargetedEmissions: Dict<number[]>,
    t: number
  ): EmissionData {
    const targetedEmissions: Dict<number> = {};
    const remainingEmissions: Dict<number> = {};
    Object.entries(yearlyTargetedEmissions).forEach(([key, dict]) => {
      targetedEmissions[key] = dict[t];
    });
    Object.keys(lhsCccsTargetedByTTs).forEach((cccKey) => {
      const cccEmissionsBau = cccsEmissionsBau[cccKey];
      const ttKeysTargeting: string[] = lhsCccsTargetedByTTs[cccKey] ?? [];
      const allTargetedEmissionsForCcc = ttKeysTargeting.map((ttKey) => {
        const cccs = activeTTsCccs[ttKey];
        const targetedEmissionsForTt = targetedEmissions[ttKey];
        const emissionsFromCCCs = sum(cccs.map((cccKey) => cccsEmissionsBau[cccKey] ?? 0));
        //we'll ignore the fact that theoretically the the ratios of the cccs might change during the transition.
        //This is done incorrectly in the old version as well.
        const part = emissionsFromCCCs === 0 ? 0 : targetedEmissionsForTt / emissionsFromCCCs;
        return targetedEmissionsForTt * part;
      });
      const totalTargetedEmissions = sum(allTargetedEmissionsForCcc);
      let remainingEmissionsForCCC = cccEmissionsBau - totalTargetedEmissions;
      if (remainingEmissionsForCCC < 0) {
        remainingEmissionsForCCC = 0;
      }
      remainingEmissions[cccKey] = remainingEmissionsForCCC;
    });
    return { targetedEmissions, remainingEmissions };
  }

  private _getTargets(board: ClientBoardContainer): CccAdapterTransitionTargetItem[] {
    const boardTargets = getTransitionTargets(board);
    const targets = boardTargets.map((target) => {
      return {
        targetLevel: target.nodeProperties.transitionTargetData.transitionProperties.targetLevel,
        transitionElement: target.nodeProperties.transitionTargetData.transitionTargetProperties.transitionElement,
        trajectoryFunctionType:
          target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.type ||
          DEFAULT_TRAJECTORY_FUNCTION_TYPE,
        manualPath: target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.manualPath,
      };
    });

    return targets;
  }

  private _getTTEmissionData({
    ttKeys,
    targetedEmissions,
    remainingEmissions,
    activeTTsCccs,
    lhsCccsTargetedByTTs,
    calculationResult,
    endYearWeightsResult,
    customModelTargetItems,
    distributeUntargetedEmissions,
    useNew,
  }: {
    ttKeys: string[];
    targetedEmissions: Dict<number>;
    remainingEmissions: Dict<number>;
    activeTTsCccs: Dict<string[]>;
    lhsCccsTargetedByTTs: Dict<string[]>;
    calculationResult: CalculationResult;
    endYearWeightsResult?: CalculationResult;
    customModelTargetItems: Dict<OneYearCustomTargetModelData>;
    distributeUntargetedEmissions: boolean;
    useNew: boolean;
  }): Dict<TTEmissionData> {
    const addTargetedEmission = (ttId: string, emissions: number) => {
      targetedEmissions[ttId] = (targetedEmissions[ttId] ?? 0) + emissions;
    };

    const cmIds = Object.keys(customModelTargetItems);
    const leftToBeTargetedByCm = new Map<string, number>();
    const activeCmsCccs: Dict<string[]> = {};
    const lhsCccsTargetedByCms: Dict<string[]> = {};

    cmIds.forEach((cmId) => {
      customModelTargetItems[cmId].lhsCccs.forEach((cccId) => {
        const ids = lhsCccsTargetedByCms[cccId] ?? [];
        lhsCccsTargetedByCms[cccId] = [...ids, cmId];
      });
      activeCmsCccs[cmId] = customModelTargetItems[cmId].lhsCccs;
    });

    const setLeftToBeTargetedByCM = (id: string, handled: number) => {
      if (leftToBeTargetedByCm.has(id)) {
        const leftToHandle = leftToBeTargetedByCm.get(id);
        const leftAfterHandled = leftToHandle - handled;
        leftToBeTargetedByCm.set(id, leftAfterHandled);
      } else {
        const totalToHandle = customModelTargetItems[id].chartData.emissions;
        const leftAfterHandled = totalToHandle - handled;
        leftToBeTargetedByCm.set(id, leftAfterHandled);
      }
    };
    const lhsCccKeys: string[] = Object.keys(lhsCccsTargetedByCms);
    //Run through the custom models with cccs to let them handle some emissions.
    lhsCccKeys.forEach((cccKey) => {
      let remaining = remainingEmissions[cccKey] || 0;
      let claimedEmissions = 0;
      const ttKeysTargeting: string[] = lhsCccsTargetedByCms[cccKey] ?? [];
      ttKeysTargeting.forEach((ttKey) => {
        if (remaining > 0) {
          const lhsCccs = activeCmsCccs[ttKey];
          // Simple way to split the emissions for one Custom model among the ccc's it's connected to.
          let targetedEmissions = customModelTargetItems[ttKey].chartData.emissions / (lhsCccs.length || 1);
          // if we can't target all the emissions we still want to target some
          if (remaining - targetedEmissions < 0) {
            targetedEmissions = remaining;
          }
          claimedEmissions += targetedEmissions;
          remaining -= claimedEmissions;
          addTargetedEmission(ttKey, targetedEmissions);
          setLeftToBeTargetedByCM(ttKey, targetedEmissions);
        } else {
          addTargetedEmission(ttKey, 0);
        }
      });
      if (remaining < 0) {
        remaining = 0;
      }
      remainingEmissions[cccKey] = remaining;
    });

    leftToBeTargetedByCm.forEach((leftToBeHandled, cmId) => {
      const lhsCccsThatHaveRemainingEmissions = activeCmsCccs[cmId].filter((cccKey) => !!remainingEmissions[cccKey]);
      // try to assign evenly until there is nothing left to handle or there are no ccc's to assign to any more
      while (lhsCccsThatHaveRemainingEmissions.length && leftToBeHandled > 0) {
        let targeted = leftToBeHandled / (lhsCccsThatHaveRemainingEmissions.length ?? 1);
        lhsCccsThatHaveRemainingEmissions.forEach((cccKey, index) => {
          const remainingForCcc = remainingEmissions[cccKey];
          if (remainingForCcc - targeted < 0) {
            targeted = remainingForCcc;
            lhsCccsThatHaveRemainingEmissions.splice(index, 1);
            remainingEmissions[cccKey] = 0;
          }
          remainingEmissions[cccKey] = remainingForCcc - targeted;
          leftToBeHandled -= targeted;
          addTargetedEmission(cmId, targeted);
        });
      }
    });

    if (distributeUntargetedEmissions) {
      const distributeEmissions = (emissions: number, weights: Dict<number>) => {
        const totalWeight = sum(Object.values(weights));
        const parts = Object.entries(weights).reduce<Dict<number>>((dict, [tt, w]) => {
          dict[tt] = totalWeight === 0 ? 0 : w / totalWeight;
          return dict;
        }, {});
        const distributed: Dict<number> = {};
        const lastIndex = Object.keys(parts).length - 1;
        Object.entries(parts).forEach(([tt, p], index) => {
          if (index === lastIndex) {
            //for the last one we add the remaining unassigned emissions to avoid losing emissions.
            //If this occurs it will be minute but might still result in a diff between actual emissions and the targeted ones.
            distributed[tt] = emissions - sum(Object.values(distributed));
          } else {
            distributed[tt] = emissions * p;
          }
        });
        return distributed;
      };
      //If the ccs hasn't been targeted fully the remaining emissions is distributed among the tts targeting it.
      //The weight is based on the unconstrained potentials, but since that sum can be larger than 1 it is weighted based on the sum of all potentials.
      Object.entries(remainingEmissions).forEach(([cccKey, remaining]) => {
        const ttKeysTargeting: string[] = lhsCccsTargetedByTTs[cccKey] ?? [];
        const potentials: Dict<number> = ttKeysTargeting.reduce((potentials, ttId) => {
          if (ttKeys.includes(ttId)) {
            if (useNew) {
              potentials[ttId] = targetedEmissions[ttId] ?? 0;
            } else {
              potentials[ttId] = endYearWeightsResult.transitions[ttId]?.potential.unconstrained ?? 0;
            }
          }
          return potentials;
        }, {});
        //TODO: Handle the case where remaining emissions ends up being negative.
        //This can occur if abatement is higher mid-plan then end of plan for some tts.
        if (remaining > 0) {
          const distributed = distributeEmissions(remaining, potentials);
          ttKeysTargeting.forEach((ttId) => {
            addTargetedEmission(ttId, distributed[ttId] ?? 0);
          });
        }
      });
    }

    const TTEmissionData: Dict<TTEmissionData> = {};
    ttKeys.forEach((ttKey) => {
      const abatement: { net?: number; gross?: number } = calculationResult.transitions[ttKey]?.carbonAbatement ?? {};
      TTEmissionData[ttKey] = {
        netCarbonAbatement: abatement.net ?? abatement.gross ?? 0,
        targetedEmissions: targetedEmissions[ttKey],
      };
    });
    cmIds.forEach((cmId) => {
      const abatement = customModelTargetItems[cmId].chartData.carbonAbatement;
      TTEmissionData[cmId] = {
        //Making sure we don't have more carbon abatement than we have emissions so we don't end up with negative emissions
        netCarbonAbatement: abatement < targetedEmissions[cmId] ? abatement : targetedEmissions[cmId],
        targetedEmissions: targetedEmissions[cmId],
      };
    });
    return TTEmissionData;
  }

  private _initCccAdapterService(
    targets: CccAdapterTransitionTargetItem[],
    timeline: BoardTimeline,
    electricityEmissionFactorType: EmissionFactorCalculationMethod,
    parameters: ClientParameter[]
  ): void {
    this._cccAdapterService.init(
      { electricityEmissionFactorType },
      targets,
      setUnsetParameters(parameters),
      timeline,
      true
    );
  }
}
